/**
 * Shared utilities for the CPT-table field reducers.
 *
 * Centralises the auth-status lookups and the small error-flag helpers that
 * are reused across the per-field reducers in this folder.
 * @module updateRowData/sharedHelpers
 */
import { MedicalPaOrdersAuthStatus } from "../../../../../../enums/medicalPaOrdersAuthStatus";
import { CptCodeWithDetailsKeys } from "../../../../../../enums/tableColumnKeys";

/**
 * Returns true when the entered string parses to a number greater than zero.
 *
 * @param value - The raw cell input.
 * @returns `true` when `parseInt(value) > 0`.
 */
export const checkIfEnteredValueIsGreaterThanZero = (
  value: string,
): boolean => {
  return parseInt(value) > 0;
};

/**
 * Clones a single field cell and overrides its `hasError` flag.
 *
 * @param rowData - The current row data.
 * @param fieldKey - The cell key to patch.
 * @param hasError - The new error state for that cell.
 * @returns The patched cell object (spread of the existing cell plus `hasError`).
 */
export const updateFieldWithError = (
  rowData: any,
  fieldKey: string,
  hasError: boolean,
) => ({
  ...rowData[fieldKey],
  hasError,
});

/**
 * Checks whether the given auth status requires field-level validation
 * (date ranges, visit counts, etc.).
 *
 * @param statusId - The current PA-required status identifier.
 * @returns `true` when status is `AuthOnFile` or `AuthByRISA`.
 */
export const isStatusRequiringValidation = (
  statusId: string | undefined,
): boolean =>
  statusId === MedicalPaOrdersAuthStatus.AuthOnFile ||
  statusId === MedicalPaOrdersAuthStatus.AuthByRISA;

/**
 * Reads the current PA-required status id from a row.
 *
 * @param rowData - The current row data.
 * @returns The `currentStatusId` of the PA-required cell, or `undefined`.
 */
export const getCurrentStatusId = (rowData: any): string | undefined =>
  rowData[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId;

/**
 * Derives the two auth-status flags used throughout the field reducers.
 *
 * @param rowData - The current row data.
 * @returns An object with `isAuthOnFile` and `isAuthByRisa` booleans.
 */
export const getAuthStatusFlags = (
  rowData: any,
): { isAuthOnFile: boolean; isAuthByRisa: boolean } => {
  const statusId = getCurrentStatusId(rowData);
  return {
    isAuthOnFile: statusId === MedicalPaOrdersAuthStatus.AuthOnFile,
    isAuthByRisa: statusId === MedicalPaOrdersAuthStatus.AuthByRISA,
  };
};
